import React, { useState, Fragment } from 'react';
import { ChevronDownIcon, ChevronUpIcon } from 'lucide-react';
interface Submission {
  timestamp: string;
  email: string;
  firstName: string;
  quizAnswers: {
    age: string | null;
    situation: string | null;
    duration: string | null;
    family: string | null;
    tried: string[];
    stopped: string | null;
    symptoms: string | null;
    commitment: number | null;
  };
}
interface SubmissionsTableProps {
  submissions: Submission[];
}
export function SubmissionsTable({
  submissions
}: SubmissionsTableProps) {
  const [expandedRow, setExpandedRow] = useState<number | null>(null);
  const formatDate = (isoString: string) => {
    const date = new Date(isoString);
    return date.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };
  const calculateResultType = (answers: Submission['quizAnswers']): string => {
    let earlyVotes = 0;
    let midVotes = 0;
    let lateVotes = 0;
    if (answers.situation === 'Mostly intact, slight recession at temples') earlyVotes++;else if (answers.situation === 'Thinning at the crown' || answers.situation === 'Both receding hairline and crown thinning') midVotes++;else if (answers.situation === 'Large visible thinning or bald patches') lateVotes++;
    if (answers.duration === 'Less than 12 months') earlyVotes++;else if (answers.duration === '1–3 years') midVotes++;else if (answers.duration === '3–5 years' || answers.duration === 'More than 5 years') lateVotes++;
    if (answers.family === 'None / very minimal') earlyVotes++;else if (answers.family === 'Some thinning but not bald' || answers.family === 'One side of family with clear balding') midVotes++;else if (answers.family === 'Both sides with clear balding') lateVotes++;
    if (lateVotes > earlyVotes && lateVotes > midVotes) return 'Late-Stage';
    if (earlyVotes > midVotes && earlyVotes > lateVotes) return 'Early-Stage';
    return 'Mid-Stage';
  };
  if (submissions.length === 0) {
    return <div className="bg-crown-surface border border-crown-border rounded-lg p-12 text-center" data-id="element-17">
        <p className="text-crown-body" data-id="element-18">No submissions yet</p>
      </div>;
  }
  return <div className="bg-crown-surface border border-crown-border rounded-lg overflow-hidden" data-id="element-19">
      <div className="overflow-x-auto" data-id="element-20">
        <table className="w-full" data-id="element-21">
          <thead className="bg-crown-bg border-b border-crown-border" data-id="element-22">
            <tr data-id="element-23">
              <th className="px-6 py-4 text-left text-xs font-semibold text-crown-body uppercase tracking-wider" data-id="element-24">
                Timestamp
              </th>
              <th className="px-6 py-4 text-left text-xs font-semibold text-crown-body uppercase tracking-wider" data-id="element-25">
                Email
              </th>
              <th className="px-6 py-4 text-left text-xs font-semibold text-crown-body uppercase tracking-wider" data-id="element-26">
                Name
              </th>
              <th className="px-6 py-4 text-left text-xs font-semibold text-crown-body uppercase tracking-wider" data-id="element-27">
                Result Type
              </th>
              <th className="px-6 py-4 text-left text-xs font-semibold text-crown-body uppercase tracking-wider" data-id="element-28">
                Details
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-crown-border" data-id="element-29">
            {submissions.map((submission, index) => <Fragment key={index} data-id="element-30">
                <tr className="hover:bg-crown-bg transition-colors" data-id="element-31">
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-crown-body" data-id="element-32">
                    {formatDate(submission.timestamp)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-crown-text font-medium" data-id="element-33">
                    {submission.email}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-crown-body" data-id="element-34">
                    {submission.firstName || '—'}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap" data-id="element-35">
                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-crown-red bg-opacity-20 text-crown-red" data-id="element-36">
                      {calculateResultType(submission.quizAnswers)}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm" data-id="element-37">
                    <button onClick={() => setExpandedRow(expandedRow === index ? null : index)} className="text-crown-red hover:text-crown-red-hover flex items-center gap-1" data-id="element-38">
                      {expandedRow === index ? <>
                          <ChevronUpIcon className="w-4 h-4" data-id="element-39" />
                          Hide
                        </> : <>
                          <ChevronDownIcon className="w-4 h-4" data-id="element-40" />
                          View
                        </>}
                    </button>
                  </td>
                </tr>
                {expandedRow === index && <tr data-id="element-41">
                    <td colSpan={5} className="px-6 py-4 bg-crown-bg" data-id="element-42">
                      <div className="space-y-3 text-sm" data-id="element-43">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-44">
                          <div data-id="element-45">
                            <span className="text-crown-muted" data-id="element-46">Age:</span>{' '}
                            <span className="text-crown-text" data-id="element-47">
                              {submission.quizAnswers.age || '—'}
                            </span>
                          </div>
                          <div data-id="element-48">
                            <span className="text-crown-muted" data-id="element-49">Situation:</span>{' '}
                            <span className="text-crown-text" data-id="element-50">
                              {submission.quizAnswers.situation || '—'}
                            </span>
                          </div>
                          <div data-id="element-51">
                            <span className="text-crown-muted" data-id="element-52">Duration:</span>{' '}
                            <span className="text-crown-text" data-id="element-53">
                              {submission.quizAnswers.duration || '—'}
                            </span>
                          </div>
                          <div data-id="element-54">
                            <span className="text-crown-muted" data-id="element-55">
                              Family History:
                            </span>{' '}
                            <span className="text-crown-text" data-id="element-56">
                              {submission.quizAnswers.family || '—'}
                            </span>
                          </div>
                          <div data-id="element-57">
                            <span className="text-crown-muted" data-id="element-58">
                              Stopped Treatment:
                            </span>{' '}
                            <span className="text-crown-text" data-id="element-59">
                              {submission.quizAnswers.stopped || '—'}
                            </span>
                          </div>
                          <div data-id="element-60">
                            <span className="text-crown-muted" data-id="element-61">Symptoms:</span>{' '}
                            <span className="text-crown-text" data-id="element-62">
                              {submission.quizAnswers.symptoms || '—'}
                            </span>
                          </div>
                          <div data-id="element-63">
                            <span className="text-crown-muted" data-id="element-64">
                              Commitment (1-10):
                            </span>{' '}
                            <span className="text-crown-text" data-id="element-65">
                              {submission.quizAnswers.commitment || '—'}
                            </span>
                          </div>
                          <div className="md:col-span-2" data-id="element-66">
                            <span className="text-crown-muted" data-id="element-67">Tried:</span>{' '}
                            <span className="text-crown-text" data-id="element-68">
                              {submission.quizAnswers.tried.length > 0 ? submission.quizAnswers.tried.join(', ') : '—'}
                            </span>
                          </div>
                        </div>
                      </div>
                    </td>
                  </tr>}
              </Fragment>)}
          </tbody>
        </table>
      </div>
    </div>;
}